import React, { useState, useEffect, useRef } from 'react';
import { X, MoreHorizontal, MicOff, Ban, Flag } from 'lucide-react';
import { supabase } from '../config/supabase';
import { getChannel, getPresenceKey } from '../services/realtimeManager';
import { useWorldStore } from '../stores/worldStore';
import { useSafetyStore, mutedIn } from '../stores/safetyStore';
import { mute, block, type SafetyTarget } from '../services/safety';
import { openReportDialog } from './safety/openSafety';
import { showSafetyToast } from '../stores/safetyUiStore';
import './ChatOverlay.css';
import './safety/safety.css';

interface Message {
  id: string;
  user: string;
  text: string;
  timestamp: Date;
  isHost?: boolean;
  /** Author's account id (null for visitors / old clients) and presence key. */
  userId?: string | null;
  key?: string | null;
}

interface ChatOverlayProps {
  worldId: string;
  onClose: () => void;
}

const ChatOverlay: React.FC<ChatOverlayProps> = ({ worldId, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const userName = useWorldStore((s) => s.userName);
  const user = useWorldStore((s) => s.user);
  const isOwner = useWorldStore((s) => s.isOwner);
  // Mute / block: re-render when the lists change; "…" menu per message.
  const safety = useSafetyStore();
  const [menuFor, setMenuFor] = useState<string | null>(null);
  // Block native DOM events on interactive elements so OrbitControls doesn't steal pointer
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const stop = (e: Event) => {
      // Only stop propagation on interactive children (input, buttons, messages)
      const target = e.target as HTMLElement;
      if (target.closest('.chat-input-wrapper') || target.closest('.chat-messages') || target.closest('.chat-header')) {
        e.stopPropagation();
      }
    };
    const events = ['pointerdown', 'pointermove', 'pointerup', 'mousedown', 'mousemove', 'mouseup', 'wheel', 'touchstart', 'touchmove', 'touchend'];
    events.forEach((evt) => el.addEventListener(evt, stop, { capture: true }));
    return () => { events.forEach((evt) => el.removeEventListener(evt, stop, { capture: true })); };
  }, []);

  // Load recent messages from DB + listen for new ones via broadcast
  useEffect(() => {
    if (!worldId) return;

    // Load last 50 messages from DB
    const loadHistory = async () => {
      try {
        const { data } = await supabase
          .from('world_messages')
          .select('*')
          .eq('world_id', worldId)
          .order('created_at', { ascending: true })
          .limit(50);
        if (data) {
          setMessages(data.map((m: any) => ({
            id: m.id,
            user: m.user_name,
            text: m.text,
            timestamp: new Date(m.created_at),
            isHost: m.is_host,
            userId: m.user_id || null,
          })));
        }
      } catch (e) {
        console.error('[Chat] Failed to load history:', e);
      }
    };
    loadHistory();

    // Listen for broadcast messages (real-time from other users)
    const channel = getChannel(worldId);
    const handleBroadcast = (payload: any) => {
      const msg = payload.payload;
      if (msg) {
        setMessages(prev => [...prev, {
          id: msg.id,
          user: msg.user,
          text: msg.text,
          timestamp: new Date(msg.timestamp),
          isHost: msg.isHost,
          userId: typeof msg.userId === 'string' ? msg.userId : null,
          key: typeof msg.key === 'string' ? msg.key : null,
        }]);
      }
    };

    channel.on('broadcast', { event: 'chat:message' }, handleBroadcast);

    return () => {
      // Channel cleanup is handled by realtimeManager on world change
    };
  }, [worldId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!inputText.trim() || !worldId) return;

    const message: Message = {
      id: Date.now().toString(),
      user: userName || 'Player',
      text: inputText,
      timestamp: new Date(),
      isHost: isOwner,
      userId: user?.id || null,
      key: getPresenceKey(),
    };

    // Broadcast to other users immediately
    const channel = getChannel(worldId);
    channel.send({
      type: 'broadcast',
      event: 'chat:message',
      payload: message,
    });

    // Add to local state
    setMessages(prev => [...prev, message]);
    setInputText('');

    // Persist to DB (fire and forget)
    try {
      await supabase.from('world_messages').insert({
        world_id: worldId,
        user_name: userName || 'Player',
        user_id: user?.id || null,
        text: inputText,
        is_host: isOwner,
      });
    } catch (e) {
      console.error('[Chat] Failed to persist message:', e);
    }
  };

  return (
    <div
      ref={panelRef}
      className="chat-overlay"
    >
      <div className="chat-header">
        <h3>Chat</h3>
        <button className="icon-btn" onClick={onClose} title="Close chat"><X size={14} /></button>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="chat-empty">No messages yet. Say hello!</div>
        )}
        {messages.filter((msg) => !mutedIn(safety, msg.userId, msg.key)).map((msg) => {
          const mine = (!!user?.id && msg.userId === user.id) || (!!msg.key && msg.key === getPresenceKey());
          const target: SafetyTarget = { userId: msg.userId || null, key: msg.key || null, name: msg.user || 'Player' };
          const canAct = !mine && (!!msg.userId || !!msg.key);
          return (
            <div key={msg.id} className={`chat-message ${msg.isHost ? 'host-message' : ''}`}>
              <span className="message-user">{msg.user}:</span>
              <span className="message-text">{msg.text}</span>
              {canAct && (
                <button className="chat-msg-more" aria-label={`Options for ${msg.user}`} aria-expanded={menuFor === msg.id}
                  onClick={() => setMenuFor(menuFor === msg.id ? null : msg.id)}><MoreHorizontal size={15} /></button>
              )}
              {canAct && menuFor === msg.id && (
                <div className="chat-msg-menu" role="menu">
                  <button role="menuitem" onClick={() => { setMenuFor(null); void mute(target); showSafetyToast(`Muted ${target.name}. You won't hear them or see their messages.`); }}>
                    <MicOff size={14} /> Mute {target.name}
                  </button>
                  <button role="menuitem" onClick={() => { setMenuFor(null); void block(target); showSafetyToast(`Blocked ${target.name}. You won't see or hear each other.`); }}>
                    <Ban size={14} /> Block
                  </button>
                  {target.userId && (
                    <button role="menuitem" className="danger" onClick={() => { setMenuFor(null); openReportDialog(target); }}>
                      <Flag size={14} /> Report
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-wrapper">
        <input
          type="text"
          className="input-glass"
          placeholder="Type a message..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
        />
        <button className="btn-primary" onClick={sendMessage}>
          Send
        </button>
      </div>
    </div>
  );
};

export default ChatOverlay;
